import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  MessageSquare,
  Shield,
  ShieldCheck,
  Search,
  Filter,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Scissors,
  Building2,
  Loader2,
  Eye,
  BadgeCheck,
  Clock
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const VerificationDashboard = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [grantBadge, setGrantBadge] = useState(false);

  useEffect(() => {
    fetchPendingVerifications();
  }, [roleFilter, statusFilter]);

  const fetchPendingVerifications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter !== "all") params.append("role_filter", roleFilter);
      if (statusFilter !== "all") params.append("status_filter", statusFilter);
      
      const response = await axios.get(`${API}/admin/verification/pending?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setCounts(response.data.counts);
    } catch (error) {
      toast.error("Failed to load verification queue");
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setActionReason("");
    setGrantBadge(action === "verify");
    setShowActionDialog(true);
  };

  const processAction = async () => {
    if (!selectedUser || !actionType) return;
    
    setActionLoading(selectedUser.user_id);
    try {
      await axios.post(`${API}/admin/verification/${selectedUser.user_id}`, {
        action: actionType,
        reason: actionReason || null,
        grant_badge: grantBadge
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(
        actionType === "verify" ? "Profile verified!" :
        actionType === "reject" ? "Profile rejected" :
        "Information requested"
      );
      
      setShowActionDialog(false);
      fetchPendingVerifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const toggleBadge = async (userId, currentBadge) => {
    setActionLoading(userId);
    try {
      const response = await axios.post(`${API}/admin/verification/${userId}/toggle-badge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.verified_badge ? "Badge granted!" : "Badge removed");
      fetchPendingVerifications();
    } catch (error) {
      toast.error("Failed to toggle badge");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status, hasBadge) => {
    const styles = {
      verified: "bg-green-100 text-green-700 border-green-200",
      auto_approved: "bg-blue-100 text-blue-700 border-blue-200",
      rejected: "bg-red-100 text-red-700 border-red-200",
      pending_info: "bg-yellow-100 text-yellow-700 border-yellow-200"
    };
    
    const labels = {
      verified: "Verified",
      auto_approved: "Auto-Approved",
      rejected: "Rejected",
      pending_info: "Info Requested"
    };
    
    return (
      <div className="flex items-center gap-2">
        <Badge className={`${styles[status] || styles.auto_approved}`}>
          {labels[status] || status}
        </Badge>
        {hasBadge && (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
            <BadgeCheck className="w-3 h-3 mr-1" />
            Verified Badge
          </Badge>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  // Filter users by search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      user.professional_type?.toLowerCase().includes(term) ||
      user.city?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            Profile Verification
          </h2>
          <p className="text-stone-400 mt-1">Review and verify stylist & owner profiles</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: "Total Profiles", value: counts.total || 0, icon: Users, color: "text-stone-400" },
          { label: "Auto-Approved", value: counts.auto_approved || 0, icon: Clock, color: "text-blue-400" },
          { label: "Verified", value: counts.verified || 0, icon: CheckCircle, color: "text-green-400" },
          { label: "Rejected", value: counts.rejected || 0, icon: XCircle, color: "text-red-400" },
          { label: "Info Requested", value: counts.pending_info || 0, icon: AlertCircle, color: "text-yellow-400" },
          { label: "With Badge", value: counts.with_badge || 0, icon: BadgeCheck, color: "text-amber-400" }
        ].map((stat, i) => (
          <Card key={i} className="bg-stone-800/50 border-stone-700">
            <CardContent className="p-4 text-center">
              <stat.icon className={`w-6 h-6 mx-auto mb-2 ${stat.color}`} />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-xs text-stone-400">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder="Search by name, email, city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-stone-900 border-stone-600 text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-36 bg-stone-900 border-stone-600 text-white">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-stone-700">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="stylist">Stylists</SelectItem>
                  <SelectItem value="owner">Owners</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 bg-stone-900 border-stone-600 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-stone-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="auto_approved">Auto-Approved</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="pending_info">Info Requested</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <Card className="bg-stone-800/50 border-stone-700">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-stone-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-stone-300">No profiles found</h3>
            <p className="text-stone-500 text-sm">Try adjusting your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredUsers.map((user) => (
            <Card key={user.user_id} className="bg-stone-800/50 border-stone-700 hover:border-amber-500/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* User Info */}
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {user.profile_photo ? (
                        <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                      ) : user.is_owner ? (
                        <Building2 className="w-6 h-6 text-amber-400" />
                      ) : (
                        <Scissors className="w-6 h-6 text-amber-400" />
                      )}
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white">{user.name}</h3>
                        <Badge variant="outline" className="text-xs border-stone-600 text-stone-300">
                          {user.is_owner ? "Owner" : "Stylist"}
                        </Badge>
                        {user.professional_type && (
                          <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">
                            {user.professional_type}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-400">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </span>
                        {user.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {user.phone}
                          </span>
                        )}
                        {(user.city || user.state) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[user.city, user.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Joined {formatDate(user.created_at)}
                        </span>
                      </div>
                      
                      {/* Status */}
                      <div className="mt-2">
                        {getStatusBadge(user.verification_status, user.verified_badge)}
                      </div>
                      
                      {/* Notes */}
                      {user.verification_notes && (
                        <p className="mt-2 text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded">
                          Note: {user.verification_notes}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap">
                    {/* Toggle Badge */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleBadge(user.user_id, user.verified_badge)}
                      disabled={actionLoading === user.user_id}
                      className={`border-amber-500/50 ${user.verified_badge ? 'text-amber-400 bg-amber-500/10' : 'text-stone-400'} hover:bg-amber-500/20`}
                    >
                      <BadgeCheck className="w-4 h-4 mr-1" />
                      {user.verified_badge ? "Remove Badge" : "Grant Badge"}
                    </Button>
                    
                    {/* Verify */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openActionDialog(user, "verify")}
                      disabled={actionLoading === user.user_id}
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Verify
                    </Button>
                    
                    {/* Request Info */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openActionDialog(user, "request_info")}
                      disabled={actionLoading === user.user_id}
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Request Info
                    </Button>
                    
                    {/* Reject */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openActionDialog(user, "reject")}
                      disabled={actionLoading === user.user_id}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === "verify" && <CheckCircle className="w-5 h-5 text-green-400" />}
              {actionType === "reject" && <XCircle className="w-5 h-5 text-red-400" />}
              {actionType === "request_info" && <MessageSquare className="w-5 h-5 text-yellow-400" />}
              {actionType === "verify" ? "Verify Profile" : actionType === "reject" ? "Reject Profile" : "Request Information"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedUser && (
              <div className="flex items-center gap-3 p-3 bg-stone-800 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  {selectedUser.is_owner ? <Building2 className="w-5 h-5 text-amber-400" /> : <Scissors className="w-5 h-5 text-amber-400" />}
                </div>
                <div>
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-stone-400">{selectedUser.email}</p>
                </div>
              </div>
            )}
            
            {actionType === "verify" && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="grant-badge"
                  checked={grantBadge}
                  onCheckedChange={setGrantBadge}
                  className="border-amber-500 data-[state=checked]:bg-amber-500"
                />
                <Label htmlFor="grant-badge" className="text-sm text-stone-300 cursor-pointer">
                  Grant Verified Badge ✓ (appears on their profile)
                </Label>
              </div>
            )}
            
            {(actionType === "reject" || actionType === "request_info") && (
              <div>
                <Label className="text-stone-300">
                  {actionType === "reject" ? "Rejection Reason" : "What information is needed?"}
                </Label>
                <Textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={actionType === "reject" 
                    ? "Explain why this profile is being rejected..." 
                    : "Specify what additional information you need..."}
                  className="mt-2 bg-stone-800 border-stone-600 text-white resize-none"
                  rows={3}
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)} className="border-stone-600 text-stone-300">
              Cancel
            </Button>
            <Button
              onClick={processAction}
              disabled={actionLoading}
              className={
                actionType === "verify" ? "bg-green-600 hover:bg-green-700" :
                actionType === "reject" ? "bg-red-600 hover:bg-red-700" :
                "bg-yellow-600 hover:bg-yellow-700"
              }
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {actionType === "verify" ? "Verify Profile" : actionType === "reject" ? "Reject Profile" : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificationDashboard;
